// ink-component
import React, { useState, useRef, useEffect, ReactNode } from 'react';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { bemHelper } from '../../helpers';

import NavItem from './NavItem/NavItem';
import Fade from '../Fade/Fade';
import DeprecatedDropdownItem from '../../deprecated/DeprecatedDropdown/DeprecatedDropdownItem/DeprecatedDropdownItem';
import DeprecatedDropdown from '../../deprecated/DeprecatedDropdown/DeprecatedDropdown';
import itemsOverflow from './helpers/itemsOverflow';

import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';
import getGlobalProps from '../../helpers/constants/global-props';

export interface Item {
  label: ReactNode;
  id: number | string;
  href?: string;
  isDropdown?: boolean;
  isStaff?: boolean;
  isButton?: boolean;
  wrapText?: boolean;
}

export interface NavProps {
  /** Unique Identifier */
  id?: string | number;
  /** Items in the nav. If the item has isDropdown, the label must be a Dropdown component. Wrap only works with vertical navs. */
  items: Item[];
  /** Vertical version of nav */
  isVertical?: boolean;
  /** Use for sub-navigation within a single page */
  isSubNav?: boolean;
  /** Adds side padding */
  hasSidePadding?: boolean;
  /** Box shadow for when the nav is sticky to the top */
  hasBorder?: boolean;
  /** Fade effect */
  hasFade?: boolean;
  /** Active id  */
  active?: number | string;
  /** onChange callback. It sends back the item that was clicked the index of that item and the event itself */
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange?: Function;
  /** Is using React Router */
  withReactRouter?: boolean;
  /** Is centralized */
  isCentered?: boolean;
  /** Remove built-in spacing */
  trim?: boolean;
  /** Always display all items */
  noHide?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

export const MORE_DROPDOWN_WIDTH = 85;
export const BREAKPOINT_MD = 768;

const globalProps = getGlobalProps({ deprecated: true, inkSass: true });

/**
 * @categories navigation
 * @purpose Creates a page's central navigation
 * @deprecated This component is deprecated and replaced by [HorizontalNav](https://ink.carta.com/components/HorizontalNav/), [VerticalNav](https://ink.carta.com/components/VerticalNav/) or [SubNav](https://ink.carta.com/components/SubNav/)
 * @status deprecated
 * @replacementComponent HorizontalNav, VerticalNav, SubNav
 * @dateOfDeletion September 1st, 2023
 */
function Nav(props: NavProps) {
  useDeprecatedWarning({ componentName: 'Nav', substitute: 'HorizontalNav, VerticalNav, or SubNav' });

  const {
    id,
    items,
    onChange,
    noHide,
    isVertical,
    isSubNav,
    hasSidePadding,
    hasBorder,
    isCentered,
    trim,
    withReactRouter,
    hasFade,
  } = props;

  const [active, setActive] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [hiddenItems, setHiddenItems] = useState<Array<Item>>([]);
  const [visibleItems, setVisibleItems] = useState<Array<Item>>([]);

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const wrapperRef = useRef<HTMLUListElement>(null);

  const setNavItems = () => {
    const wrapperNode = wrapperRef.current;

    const nativeItems = wrapperNode ? wrapperNode.querySelectorAll('li[id]') : [];
    const widthsArray = [...nativeItems].map(item => item.getBoundingClientRect().width);

    const boundingClientRect = wrapperNode && wrapperNode.getBoundingClientRect();
    const rectWidth = boundingClientRect ? boundingClientRect.width : 0;

    const outerWidth = Math.max(rectWidth - MORE_DROPDOWN_WIDTH, MORE_DROPDOWN_WIDTH);

    const visibleCount = itemsOverflow(widthsArray, outerWidth);

    setIsVisible(true);
    setVisibleItems(items.slice(0, visibleCount));
    setHiddenItems(items.slice(visibleCount));
  };

  const updateOnResize = () => {
    setIsVisible(false);
    setVisibleItems([]);
    setHiddenItems([]);

    setNavItems();
  };

  useEffect(() => {
    const resizeHandler = debounce(updateOnResize, 10);
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  });

  useEffect(() => {
    setIsVisible(false);
    setVisibleItems(items);
    setHiddenItems([]);

    setNavItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleClick = (nextActive, index, e) => {
    setActive(nextActive.id);
    if (onChange) {
      onChange(nextActive, index, e);
    }
  };

  const getItemsForDropdown = () => {
    return hiddenItems.map((item, index) => (
      <DeprecatedDropdownItem
        key={index}
        href={item.href}
        withReactRouter={props.withReactRouter}
        onClick={e => handleClick(item, index, e)}
      >
        {item.label}
      </DeprecatedDropdownItem>
    ));
  };

  const isActive = (item, nextActive) => {
    if (nextActive) {
      return item.id === nextActive || `/${item.id}` === nextActive;
    }
    return active === item.id;
  };

  const displayOverflowItems = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < BREAKPOINT_MD;
    return isVertical || isMobile || noHide || !isVisible;
  };

  const defineClassName = () => {
    const b = bemHelper();

    const verticalOrientation = isVertical && !isSubNav && !hasBorder;

    return b('ink-nav', {
      vertical: verticalOrientation,
      hidden: !isVisible,
      subnav: isSubNav,
      padded: hasSidePadding,
      borderline: !verticalOrientation,
      'top-border': hasBorder,
      center: isCentered,
      trim,
      auto: !isEmpty(items) || isVertical || isSubNav,
    });
  };

  const mainContent = () => {
    const navItems = displayOverflowItems() ? items : visibleItems;

    return (
      <ul
        {...globalProps}
        id={String(id)}
        className={defineClassName()}
        ref={wrapperRef}
        data-testid={props['data-testid']}
      >
        {navItems.map((item, index) => {
          // @ts-ignore
          if (item.isDropdown && item.label.type.displayName !== 'DeprecatedDropdown') {
            throw new Error('Nav: item label must be an Ink.DeprecatedDropdown if isDropdown is true.');
          }
          return (
            <NavItem
              id={String(item.id)}
              key={index}
              label={item.label}
              href={item.href}
              isVertical={isVertical && !isSubNav && !hasBorder}
              isActive={isActive(item, props.active)}
              onClick={e => handleClick(item, index, e)}
              withReactRouter={withReactRouter}
              isDropdown={item.isDropdown}
              isButton={item.isButton}
              isStaff={item.isStaff}
              wrapText={item.wrapText}
            />
          );
        })}
        {!displayOverflowItems() && !!hiddenItems.length && (
          <NavItem
            id="more"
            key="more"
            isDropdown
            label={
              <DeprecatedDropdown trigger="More" align="right" hasArrow>
                {getItemsForDropdown()}
              </DeprecatedDropdown>
            }
          />
        )}
      </ul>
    );
  };

  if (hasFade && !isSubNav) {
    return <Fade isVertical={isVertical}>{mainContent()}</Fade>;
  }

  return <div className="wrapper-nav">{mainContent()}</div>;
}

Nav.defaultProps = {
  id: undefined,
  isVertical: false,
  isSubNav: false,
  hasSidePadding: false,
  hasBorder: false,
  hasFade: false,
  active: undefined,
  onChange: () => {},
  withReactRouter: false,
  isCentered: false,
  trim: false,
  noHide: false,
  'data-testid': undefined,
};

Nav.displayName = 'Nav';

export default Nav;
